import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import { FormApi } from 'final-form';

import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { ISelectInputFieldProps, SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import {
  knmFilterFormApprovedSelectOptions,
  knmFilterFormBooleanSelectOptions,
  knmFilterFormNotifyLateSelectOptions,
  knmFilterFormSignedSelectOptions,
  noKnoOrganizationAdminLevelSelectOption,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { publishedStatusesSelectOptions } from 'NewsPackage/MainModule/constants/publishedStatuses';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { ISetFilterCurrentParams } from 'CorePackage/FiltersModalModule/interfaces/ISetFilterCurrentParams';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import {
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IFederalLawSelectOption } from 'CorePackage/CatalogsModule/interfaces/IFederalLawSelectOption';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IMspCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IMspCategorySelectOption';
import { IRegisterTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IRegistryTypeSelectOption';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  geCatalogsResponsibilityTypesSelectOptions,
  getCatalogKnmStatuses248SelectOptions,
  getCatalogKnmSubjectTypesSelectOptions,
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogKnmTypes294SelectOptions,
  getCatalogKnmYearsSelectOptions,
  getCatalogKnoAdminLevelsSelectOptions,
  getCatalogsChecklistFullnessTypesSelectOptions,
  getCatalogsMspCategoriesSelectOptions,
  getCatalogsRegisterTypesSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
  getFederalLaw248SelectOptions,
  getUserKnoSelectOptions,
  getUserProsecutorSelectOptions,
  makeGetCatalogsSupervisionTypesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import {
  getKnmDataPlanId,
  getKnmErpStatusesSelectOptions,
  getKnmFederalLawsSelectOptions,
  getKnmIsTypeErknmPlanned,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmSubjectTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IChecklistFullnessTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IChecklistFullnessTypeSelectOption';
import { IKnoAdminLevelSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnoAdminLevelSelectOption';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { knmListGetData } from 'KnmListPackage/MainModule/actions/knmListActions';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import {
  getDictionariesAllKnmKindsSelectOptions,
  getDictionariesKnmSubjectTypesSelectOptions,
  getDictionariesKnoOrganizationsSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { approveRequiredSelectOptionsForFilter } from 'KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { IKnoLevelErvk } from 'APIPackage/CatalogsModule/interfaces/IKnoLevelErvk';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';

import { convertFilterDataToControlOrganizationsIds } from '../common/convertFilterDataToControlOrganizationsIds';
import { initialKnmListFilter } from '../constants/initialKnmListFilter';
import {
  IKnmListFilterSetCurrentParams,
  knmListFilterCreate,
  knmListFilterGetData,
  knmListFilterInit,
  knmListFilterRemove,
  knmListFilterSetCurrent,
  knmListFilterSetCurrentControlOrganizations,
  knmListFilterSetCurrentFederalLaws,
  knmListFilterSetMeta,
  knmListFilterSetSwitcher,
  knmListFilterUpdate,
} from '../actions/knmListFilterActions';
import styles from './KnmListFiltersModal.module.scss';
import { IKnmListFiltersForm } from '../interfaces/IKnmListFiltersForm';
import { IKnmListFilterExtraField } from '../interfaces/IKnmListFilterExtraField';
import { IKnmListFilter } from '../interfaces/IKnmListFilter';
import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import { exclusionErknmAdditionalFields } from '../constants/exclusionErknmAdditionalFields';
import {
  initialKnmListAdditionalFields as initialKnmListAdditionalFieldsConstant,
  isTestAdditionalField,
} from '../constants/initialKnmListAdditionalFields';
import { exclusionErpAdditionalFields } from '../constants/exclusionErpAdditionalFields';
import { IKnmListFilterWithMeta } from '../interfaces/IKnmListFilterWithMeta';
import {
  getKnmListCurrentControlOrganizations,
  getKnmListCurrentFederalLaws,
  getKnmListFilterCurrentFilter,
  getKnmListFilterCurrentId,
  getKnmListFilterFilters,
} from '../selectors/knmListFilterSelectors';
import { IResponsibilityTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IResponsibilityTypeSelectOption';
import { IKnmSubjectTypesERVK } from '../../../APIPackage/CatalogsModule/interfaces/IKnmSubjectTypesERVK';
import { IKnmSubjectTypeCommonSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeCommonSelectOptions';

const mapFilter = (filter: IKnmListFilter, filterName: string): IKnmListFilter => {
  return {
    ...filter,
    name: filterName,
  };
};

interface IKnmListFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IKnmListFiltersModalStoreProps {
  yearsSelectOptions: IYearSelectOption[];
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
  federalLawsErknmSelectOptions: IFederalLawSelectOption[];
  federalLawsErpSelectOptions: IDictionarySelectOption<IFederalLawErvk>[] | undefined;
  knmTypesSelectOptionsErknm: IKnmTypeSelectOption[];
  knmTypesSelectOptionsErp: IKnmTypeSelectOption[];
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  isProsecutorOrSuperProsecutor: boolean;
  filters: IKnmListFilterWithMeta[];
  currentFilter: IKnmListFilter | undefined;
  activeFilterId: number | undefined;
  isFederalLaw248: boolean;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  checklistFullnessTypesSelectOptions: IChecklistFullnessTypeSelectOption[];
  registerTypesSelectOptions: IRegisterTypeSelectOption[];
  knmStatusesSelectOptionsErknm: IKnmStatusSelectOption[];
  knmStatusesSelectOptionsErp: IKnmStatusSelectOption[];
  knmSubjectTypesSelectOptions: IKnmSubjectTypeSelectOption[];
  knmSubjectTypesErvkSelectOptions: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined;
  formKnoOrganizationAdminLevelsSelectOptions: IKnoAdminLevelSelectOption[];
  formKnoOrganizationAdminLevelsSelectOptionsErvk: IDictionarySelectOption<IKnoLevelErvk>[] | undefined;
  mspCategoriesSelectOptions: IMspCategorySelectOption[];
  currentYearSelectOption: IYearSelectOption | undefined;
  erknmSupervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  responsibilityTypesSelectOptions: IResponsibilityTypeSelectOption[] | undefined;
  isUserTest: boolean;
  isCoreLoading: boolean;
}

const KnmListFiltersModalWithoutStore = ({
  visible,
  onClose,
  activeFilterId,
  checklistFullnessTypesSelectOptions,
  currentFilter,
  federalLawsErknmSelectOptions,
  federalLawsErpSelectOptions,
  filters,
  formKnoOrganizationAdminLevelsSelectOptions,
  formKnoOrganizationAdminLevelsSelectOptionsErvk,
  currentYearSelectOption,
  isFederalLaw248,
  knmStatusesSelectOptionsErknm,
  knmStatusesSelectOptionsErp,
  knmSubjectTypesSelectOptions,
  knmSubjectTypesErvkSelectOptions,
  knmTypesSelectOptionsErknm,
  knmTypesSelectOptionsErp,
  mspCategoriesSelectOptions,
  registerTypesSelectOptions,
  isProsecutorOrSuperProsecutor,
  supervisionTypesSelectOptions,
  territorialUnitsSelectOptions,
  userKnoOrganisationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  yearsSelectOptions,
  erknmSupervisionTypesSelectOptions,
  erknmOrganisationsSelectOptions,
  kindsSelectOptions,
  responsibilityTypesSelectOptions,
  isUserTest,
  isCoreLoading,
}: IKnmListFiltersModalProps & IKnmListFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const fromRef = useRef<FormApi<IKnmListFiltersForm, Partial<IKnmListFiltersForm>>>();

  const supervisionTypesSelectOptionsWithIds = useMemo(() => {
    return supervisionTypesSelectOptions?.map(sup => {
      return { ...sup, label: sup.digitCode ? sup.digitCode.toString().concat(` ${sup.label}`) : sup.label };
    });
  }, [supervisionTypesSelectOptions]);

  useUnmount(() => {
    dispatch(knmListFilterSetMeta([]));
  });

  const getKnmListData = useCallback(() => dispatch(knmListGetData()), [dispatch]);
  const getKnmListFilterData = useCallback((id: IFilterResult['id']) => dispatch(knmListFilterGetData(id)), [dispatch]);
  const setKnmListFilterCurrent = useCallback(
    (params: IKnmListFilterSetCurrentParams) => dispatch(knmListFilterSetCurrent(params)),
    [dispatch],
  );
  const setKnmListFilterSwitcher = useCallback((value: boolean) => dispatch(knmListFilterSetSwitcher(value)), [
    dispatch,
  ]);
  const createKnmListFilter = useCallback((data: IKnmListFilter) => dispatch(knmListFilterCreate(data)), [dispatch]);
  const updateKnmListFilter = useCallback(
    (id: IFilterResult['id'], data: IKnmListFilter) => dispatch(knmListFilterUpdate(id, data)),
    [dispatch],
  );
  const removeKnmListFilter = useCallback((id: IFilterResult['id']) => dispatch(knmListFilterRemove(id)), [dispatch]);

  const setCurrentControlOrganizations = useCallback(
    (ids: string[] | undefined) => dispatch(knmListFilterSetCurrentControlOrganizations(ids || [])),
    [dispatch],
  );

  const setCurrentFederalLaws = useCallback(
    (federalLaws: IFederalLawErvk[] | undefined) => dispatch(knmListFilterSetCurrentFederalLaws(federalLaws || [])),
    [dispatch],
  );

  const defaultTerritorialUnitSelectOption = useMemo(
    () => territorialUnitsSelectOptions.find(selectOption => selectOption.isDefault),
    [territorialUnitsSelectOptions],
  );

  const initialKnmListAdditionalFields = useMemo(() => {
    let result;

    if (isFederalLaw248) {
      result = initialKnmListAdditionalFieldsConstant.filter(item => !exclusionErknmAdditionalFields.includes(item.id));
    } else {
      result = initialKnmListAdditionalFieldsConstant.filter(item => !exclusionErpAdditionalFields.includes(item.id));
    }

    if (isUserTest && !initialKnmListAdditionalFieldsConstant.find(i => i.id === isTestAdditionalField.id)) {
      result.push(isTestAdditionalField);
    }

    return result;
  }, [isFederalLaw248, isUserTest]);

  const federalLawsSelectOptions = useMemo(
    () => (isFederalLaw248 ? federalLawsErknmSelectOptions : federalLawsErpSelectOptions),
    [federalLawsErknmSelectOptions, federalLawsErpSelectOptions, isFederalLaw248],
  );

  const knmStatusesSelectOptions = useMemo(
    () => (isFederalLaw248 ? knmStatusesSelectOptionsErknm : knmStatusesSelectOptionsErp),
    [isFederalLaw248, knmStatusesSelectOptionsErknm, knmStatusesSelectOptionsErp],
  );

  const knmTypesSelectOptions = useMemo(
    () => (isFederalLaw248 ? knmTypesSelectOptionsErknm : knmTypesSelectOptionsErp),
    [isFederalLaw248, knmTypesSelectOptionsErknm, knmTypesSelectOptionsErp],
  );

  const knmSubjectTypesCommonSelectOptions = useMemo<IKnmSubjectTypeCommonSelectOption[]>(
    () => [...mapSubjectTypes(knmSubjectTypesSelectOptions), ...mapErvkSubjectTypes(knmSubjectTypesErvkSelectOptions)],
    [knmSubjectTypesSelectOptions, knmSubjectTypesErvkSelectOptions],
  );

  function mapSubjectTypes(items: IKnmSubjectTypeSelectOption[] | undefined): IKnmSubjectTypeCommonSelectOption[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(item => ({
      id: item.id,
      name: item.name,
      code: item.code,
      title: item.name,
      label: item.label,
      value: item.code,
    }));
  }

  function mapErvkSubjectTypes(
    items: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined,
  ): IKnmSubjectTypeCommonSelectOption[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter(item => item.code !== null)
      .map(item => ({
        id: item.id,
        name: item.title,
        code: item.code ?? '',
        title: item.title,
        label: item.label,
        value: item.code ?? '',
      }));
  }

  const mapper = useMemo(() => mapFilterAdditionalFields(isFederalLaw248, isUserTest), [isFederalLaw248, isUserTest]);

  const defaultKnoOrganisationSelectOption = useMemo(
    () =>
      isFederalLaw248
        ? erknmOrganisationsSelectOptions?.find(
            item =>
              item.id.toString() ===
              userKnoOrganisationsSelectOptions
                ?.find(userKnoOrganisationsSelectOption => userKnoOrganisationsSelectOption.byDefault)
                ?.id.toString(),
          )
        : userKnoOrganisationsSelectOptions?.find(
            userKnoOrganisationsSelectOption => userKnoOrganisationsSelectOption.byDefault,
          ),
    [erknmOrganisationsSelectOptions, isFederalLaw248, userKnoOrganisationsSelectOptions],
  );

  const memoizedInitialKnmListFilter: IKnmListFilter = useMemo(
    () => ({
      name: 'Новый фильтр',
      years: currentYearSelectOption ? [currentYearSelectOption] : [],
      ...(defaultTerritorialUnitSelectOption && { domains: [defaultTerritorialUnitSelectOption] }),
      includeDomainChild: false,
      ignoreKno: false,
      ...(federalLawsSelectOptions?.length && { federalLaws: federalLawsSelectOptions }),
      ...(knmTypesSelectOptions.length && { knmTypes: knmTypesSelectOptions }),
      includeChildKno: false,
      ...(isFederalLaw248 &&
        defaultKnoOrganisationSelectOption && { controllingOrganizations: [defaultKnoOrganisationSelectOption] }),
      ...(isFederalLaw248 && kindsSelectOptions && kindsSelectOptions.length && { kinds: kindsSelectOptions }),
    }),
    [
      currentYearSelectOption,
      defaultKnoOrganisationSelectOption,
      defaultTerritorialUnitSelectOption,
      federalLawsSelectOptions,
      isFederalLaw248,
      kindsSelectOptions,
      knmTypesSelectOptions,
    ],
  );

  const [formValues, setFormValues] = useState<IKnmListFiltersForm>(initialKnmListFilter);

  useEffect(() => {
    dispatch(knmListFilterInit());
  }, [dispatch]);

  const memoizedFilterSelectFields: Omit<IKnmListFilterExtraField, 'isSelected' | 'name'>[] = useMemo(
    () => [
      {
        id: 'disapprove',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'supervisionType',
        options: isFederalLaw248 ? erknmSupervisionTypesSelectOptions : supervisionTypesSelectOptionsWithIds,
      },
      {
        id: 'hasViolations',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'dataNotEqualEGRUL',
        type: 'select',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'chlistFillingIndicationType',
        options: checklistFullnessTypesSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'knmAnnulled',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'createdByMe',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'regType',
        options: registerTypesSelectOptions,
      },
      {
        id: 'prosecutorOrganizations',
        options: userProsecutorOrganisationsSelectOptions,
      },
      {
        id: 'dataSetNotInTime',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'deletedOnly',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'deletedInclude',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'orderDone',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'recEmptyOrLate',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'mspCategory',
        options: mspCategoriesSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'regLate',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'resultEmpty',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'resultEmptyOrLate',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'appealed',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'noPunishment',
        options: knmFilterFormBooleanSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'approved',
        options: knmFilterFormApprovedSelectOptions,
      },
      {
        id: 'statuses',
        options: knmStatusesSelectOptions,
      },
      {
        id: 'signed',
        options: knmFilterFormSignedSelectOptions,
      },
      {
        id: 'pubStatuses',
        options: publishedStatusesSelectOptions,
      },
      {
        id: 'subjectTypes',
        options: knmSubjectTypesCommonSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'notifyLate',
        options: knmFilterFormNotifyLateSelectOptions,
        disabled: isFederalLaw248,
      },
      {
        id: 'admLevel',
        options: formKnoOrganizationAdminLevelsSelectOptionsErvk,
        disabled: isFederalLaw248,
      },
      {
        id: 'approveRequiredType',
        options: approveRequiredSelectOptionsForFilter,
        disabled: isFederalLaw248,
      },
      {
        id: 'approvedRequired',
        options: knmFilterFormBooleanSelectOptions,
        disabled: !isFederalLaw248,
      },
      {
        id: 'responsibilityTypeCodes',
        options: responsibilityTypesSelectOptions,
      },
      {
        id: 'isTest',
        options: knmFilterFormBooleanSelectOptions,
      },
    ],
    [
      checklistFullnessTypesSelectOptions,
      erknmSupervisionTypesSelectOptions,
      formKnoOrganizationAdminLevelsSelectOptions,
      formKnoOrganizationAdminLevelsSelectOptionsErvk,
      isFederalLaw248,
      knmStatusesSelectOptions,
      knmSubjectTypesSelectOptions,
      mspCategoriesSelectOptions,
      registerTypesSelectOptions,
      supervisionTypesSelectOptionsWithIds,
      userProsecutorOrganisationsSelectOptions,
      responsibilityTypesSelectOptions,
    ],
  );

  const [additionalFields, setAdditionalFields] = useState(initialKnmListAdditionalFields);

  const onTemplateCreate = useCallback(() => {
    if (isFederalLaw248) {
      const ids = convertFilterDataToControlOrganizationsIds(
        erknmOrganisationsSelectOptions,
        memoizedInitialKnmListFilter?.controllingOrganizations,
      );

      setCurrentControlOrganizations(ids || []);
      setCurrentFederalLaws([]); // Очищаем уже сохраненные federalLaws для ЕРКНМ
    } else {
      setCurrentFederalLaws(memoizedInitialKnmListFilter.federalLaws);
    }

    const newData = {
      ...initialKnmListFilter,
      ...memoizedInitialKnmListFilter,
    };

    setFormValues(newData);
    const filter = mapFilter(newData, 'Новый фильтр');
    const filterData: ISetFilterCurrentParams<IKnmListFilter> = {
      current: { ...filter, isTemplateFirstCreate: true },
      currentId: undefined,
    };

    setKnmListFilterCurrent(filterData);
    setAdditionalFields(initialKnmListAdditionalFields);
  }, [
    setCurrentControlOrganizations,
    erknmOrganisationsSelectOptions,
    initialKnmListAdditionalFields,
    isFederalLaw248,
    memoizedInitialKnmListFilter,
    setKnmListFilterCurrent,
  ]);

  useEffect(() => {
    const newData = {
      ...initialKnmListFilter,
      ...memoizedInitialKnmListFilter,
      isTemplateFirstCreate: true,
    };

    const isDirtyForm = fromRef.current?.getState()?.dirty;

    /**
     * Обновляем template только в момент первичной загрузки,
     * для избежания бага сброса фильтров в момент заполнения
     */
    if (
      !equal(formValues, newData) &&
      activeFilterId === undefined &&
      formValues.isTemplateFirstCreate &&
      !isDirtyForm &&
      isCoreLoading
    ) {
      onTemplateCreate();
    }
  }, [activeFilterId, memoizedInitialKnmListFilter, isCoreLoading]);

  const removeAdditionalField = (name: IKnmListFilterExtraField['id']) => {
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  const validate = useCallback((values: IKnmListFiltersForm) => {
    const { amountOfPunishmentFrom, amountOfPunishmentTo } = values;

    return {
      amountOfPunishmentFrom:
        amountOfPunishmentFrom &&
        amountOfPunishmentTo &&
        Number.parseFloat(amountOfPunishmentFrom.replace(',', '.')) >
          Number.parseFloat(amountOfPunishmentTo.replace(',', '.'))
          ? 'Значение, введенное в поле «с», должно быть равно или быть строго меньше значения, введенного в поле «до»'
          : undefined,
    };
  }, []);

  function renderFormContent({ values, form }: FormRenderProps<IKnmListFiltersForm>) {
    fromRef.current = form;

    const makeRemoveFilterHandler = (name: IKnmListFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      if (name === 'startDateBetween') {
        form.change('startDateBetweenStart');
        form.change('startDateBetweenEnd');
      } else if (name === 'assignmentDateBetween') {
        form.change('assignmentDateBetweenStart');
        form.change('assignmentDateBetweenEnd');
      } else if (name === 'amountOfPunishmentBetween') {
        form.change('amountOfPunishmentFrom');
        form.change('amountOfPunishmentTo');
      } else {
        form.change(name);
      }
    };

    function handleChangeControllingOrganizations(value: IDictionarySelectOption<IKnoOrganizationDictionary>[]) {
      const ids = convertFilterDataToControlOrganizationsIds(erknmOrganisationsSelectOptions, value);

      dispatch(knmListFilterSetCurrentControlOrganizations(ids || []));
    }

    const handleChangeFederalLaws = useCallback(
      (value: IDictionarySelectOption<IFederalLawErvk>[]) => {
        dispatch(knmListFilterSetCurrentFederalLaws(value || []));
      },
      [dispatch],
    );

    function isSelectInputFieldMultiClearableEqual(
      a: ISelectOption[] | null | undefined,
      b: ISelectOption[] | null | undefined,
    ) {
      const newA = a?.length === 0 ? undefined : a;
      const newB = b?.length === 0 ? undefined : b;

      return equal(newA, newB);
    }

    function validateControllingOrganizationsSelect(value: ISelectInputFieldProps['value']) {
      if (form.getState().values.ignoreKno) {
        return undefined;
      }

      return makeRequired('Поле не может быть пустым')(value);
    }

    function checkSameOptions(option: ISelectOption, selectValue: ISelectOption[]) {
      return selectValue.some(item => equal(option, item));
    }

    const primaryContent = (
      <>
        <Row>
          <FieldBlock label="Год проведения КНМ (На основе даты проведения КНМ)" labelFontSize="small">
            <SelectInputField
              id="years"
              name="years"
              options={yearsSelectOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Территориальная единица" labelFontSize="small">
            <Row>
              <FieldBlock>
                <SelectInputField
                  id="domains"
                  name="domains"
                  options={territorialUnitsSelectOptions}
                  isSearchable
                  isMulti
                  isEqual={isSelectInputFieldMultiClearableEqual}
                  validate={makeRequired('Поле не может быть пустым')}
                />
              </FieldBlock>
            </Row>
            <CheckboxField
              id="includeDomainChild"
              name="includeDomainChild"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </FieldBlock>
        </Row>
        {!isFederalLaw248 && (
          <Row>
            <FieldBlock label="Федеральный закон, в соответствии с которым проводится КНМ" labelFontSize="small">
              <SelectInputField
                id="federalLaws"
                name="federalLaws"
                options={federalLawsSelectOptions}
                isMulti
                isSearchable
                isClearable={false}
                isEqual={equal}
                isOptionSelected={checkSameOptions}
                validate={makeRequired('Поле не может быть пустым')}
                onChange={handleChangeFederalLaws}
              />
            </FieldBlock>
          </Row>
        )}
        <Row>
          <FieldBlock label="Характер КНМ" labelFontSize="small">
            <SelectInputField
              id="knmTypes"
              name="knmTypes"
              options={knmTypesSelectOptions}
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        {isFederalLaw248 && (
          <Row border="bottom">
            <FieldBlock label="Вид КНМ" labelFontSize="small">
              <SelectInputField
                id="kinds"
                name="kinds"
                options={kindsSelectOptions}
                isMulti
                isClearable={false}
                isEqual={equal}
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
          </Row>
        )}
        <FieldBlock label="Наименование органа контроля (надзора)" labelFontSize="small">
          <Row>
            <FieldBlock>
              <SelectInputField
                id="controllingOrganizations"
                name="controllingOrganizations"
                options={erknmOrganisationsSelectOptions}
                disabled={values.ignoreKno}
                isMulti
                isSearchable
                isEqual={isSelectInputFieldMultiClearableEqual}
                isOptionSelected={checkSameOptions}
                validate={validateControllingOrganizationsSelect}
                onChange={handleChangeControllingOrganizations}
              />
            </FieldBlock>
          </Row>
          <Row>
            <CheckboxField
              id="includeChildKno"
              name="includeChildKno"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </Row>
          {isProsecutorOrSuperProsecutor && (
            <CheckboxField
              id="ignoreKno"
              name="ignoreKno"
              label="Не учитывать орган контроля"
              theme="system"
              type="checkbox"
            />
          )}
        </FieldBlock>
      </>
    );
    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.name}
                labelFontSize="small"
                type="flex-label"
                button={<CloseButton onClick={makeRemoveFilterHandler(field.id)} />}
              >
                {field.id === 'address' && (
                  <KnmAddressField id={field.id} addressFieldPathWithPrefix={field.id} required />
                )}
                {field.type === 'checkbox' && (
                  <CheckboxField id={field.id} name={field.id} theme="system" type="checkbox" />
                )}
                {field.type === 'text' && field.id !== 'amountOfPunishmentBetween' && (
                  <TextInputField name={field.id} type="text" validate={makeRequired('Поле не может быть пустым')} />
                )}
                {field.type === 'date' && field.id === 'startDateBetween' && (
                  <div className={styles.FilterFormGroup}>
                    С&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="startDateBetweenStart"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                    по&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="startDateBetweenEnd"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                  </div>
                )}
                {field.type === 'date' && field.id === 'assignmentDateBetween' && (
                  <div className={styles.FilterFormGroup}>
                    С&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="assignmentDateBetweenStart"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                    по&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="assignmentDateBetweenEnd"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                  </div>
                )}
                {field.type === 'date' && field.id !== 'startDateBetween' && field.id !== 'assignmentDateBetween' && (
                  <DatePickerField
                    name={field.id}
                    opensOnTop
                    isEqual={equal}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.type === 'select' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    options={memoizedFilterSelectFields.find(item => item.id === field.id)?.options}
                    isSearchable={field.isSearchable}
                    isMulti={field.isMulti}
                    isClearable={field.isClearable || false}
                    isEqual={field.isMulti && field.isClearable ? isSelectInputFieldMultiClearableEqual : equal}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.type === 'text' && field.id === 'amountOfPunishmentBetween' && (
                  <div className={styles.FilterFormGroup}>
                    <FieldBlock label="С" placementLabel="left" color="black" className={styles.FieldWrapper}>
                      <TextInputField
                        className={styles.AmountOfPunishmentBetweenField}
                        name="amountOfPunishmentFrom"
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                        type="number"
                      />
                    </FieldBlock>
                    <FieldBlock label="до" placementLabel="left" color="black" className={styles.FieldWrapper}>
                      <TextInputField
                        className={styles.AmountOfPunishmentBetweenField}
                        name="amountOfPunishmentTo"
                        isEqual={equal}
                        type="number"
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </FieldBlock>
                  </div>
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent,
    };
  }

  return (
    <FiltersModal<IKnmListFiltersForm, IKnmListFilter>
      visible={visible}
      initialFormValues={formValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={mapper}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getKnmListData}
      getFilterData={getKnmListFilterData}
      setFilterCurrent={setKnmListFilterCurrent}
      setFilterSwitcher={setKnmListFilterSwitcher}
      createFilter={createKnmListFilter}
      updateFilter={updateKnmListFilter}
      removeFilter={removeKnmListFilter}
      formValidate={validate}
    />
  );
};

const mapStateToProps = (state: IRootState): IKnmListFiltersModalStoreProps => {
  const isFederalLaw248 = getLawInterface(state) === LawInterfacesEnum.FZ_248;
  const knmTypesSelectOptionsErknm = getCatalogKnmTypes248SelectOptionsSelector(state)({});
  const knmTypesSelectOptionsErp = getCatalogKnmTypes294SelectOptions(state);
  const knmStatusesSelectOptionsErknm = getCatalogKnmStatuses248SelectOptions(state);
  const knmStatusesSelectOptionsErp: IKnmStatusSelectOption[] | undefined = getKnmErpStatusesSelectOptions(state);
  const knoOrganizationAdminLevelsSelectOptions = getCatalogKnoAdminLevelsSelectOptions(state);
  const knmYearsSelectOptions: IYearSelectOption[] = getCatalogKnmYearsSelectOptions(state);

  const currentYear = new Date().getFullYear();
  const nextYear = String(currentYear + 1);
  const currentYearSelectOption = knmYearsSelectOptions.find(
    selectOption => selectOption.value === String(currentYear),
  );

  const nextYearSelectOption = knmYearsSelectOptions.find(selectOption => selectOption.value === nextYear);
  const yearsSelectOptions = nextYearSelectOption
    ? knmYearsSelectOptions
    : [...knmYearsSelectOptions, { id: nextYear, label: nextYear, value: nextYear }];

  const controllingOrganizations = getKnmListCurrentControlOrganizations(state);
  const federalLaws = getKnmListCurrentFederalLaws(state);

  const federalLawUUIDs = federalLaws && federalLaws?.length ? federalLaws.map(fl => fl.id) : undefined;

  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
  const getCatalogsSupervisionTypesSelectOptions = makeGetCatalogsSupervisionTypesSelectOptions();

  return {
    activeFilterId: getKnmListFilterCurrentId(state),
    checklistFullnessTypesSelectOptions: getCatalogsChecklistFullnessTypesSelectOptions(state),
    currentFilter: getKnmListFilterCurrentFilter(state),
    federalLawsErknmSelectOptions: getFederalLaw248SelectOptions(state),
    federalLawsErpSelectOptions: getKnmFederalLawsSelectOptions(state, false),
    filters: getKnmListFilterFilters(state),
    formKnoOrganizationAdminLevelsSelectOptions: [
      ...knoOrganizationAdminLevelsSelectOptions,
      noKnoOrganizationAdminLevelSelectOption,
    ],
    formKnoOrganizationAdminLevelsSelectOptionsErvk: getCatalogKnoAdminLevelsSelectOptions(state),
    isFederalLaw248,
    knmStatusesSelectOptionsErknm,
    knmStatusesSelectOptionsErp,
    knmSubjectTypesSelectOptions: getCatalogKnmSubjectTypesSelectOptions(state),
    knmSubjectTypesErvkSelectOptions: getDictionariesKnmSubjectTypesSelectOptions(state),
    knmTypesSelectOptionsErknm,
    knmTypesSelectOptionsErp,
    mspCategoriesSelectOptions: getCatalogsMspCategoriesSelectOptions(state),
    registerTypesSelectOptions: getCatalogsRegisterTypesSelectOptions(state),
    isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
    supervisionTypesSelectOptions: getDictionariesSupervisionTypesSelectOptions(
      state,
      controllingOrganizations,
      false,
      false,
      federalLawUUIDs,
    )
      ? [
          ...getDictionariesSupervisionTypesSelectOptions(
            state,
            controllingOrganizations,
            false,
            false,
            federalLawUUIDs,
          )!,
          ...getCatalogsSupervisionTypesSelectOptions(state, controllingOrganizations),
        ]
      : getCatalogsSupervisionTypesSelectOptions(state, controllingOrganizations),
    territorialUnitsSelectOptions: getCatalogsTerritorialUnitsSelectOptions(state),
    userKnoOrganisationsSelectOptions: getUserKnoSelectOptions(state),
    userProsecutorOrganisationsSelectOptions: getUserProsecutorSelectOptions(state),
    yearsSelectOptions,
    currentYearSelectOption,
    erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    erknmSupervisionTypesSelectOptions: getDictionariesSupervisionTypesSelectOptions(
      state,
      controllingOrganizations || [],
      false,
      getKnmIsTypeErknmPlanned(state) || !!getKnmDataPlanId(state),
    ),
    kindsSelectOptions: getDictionariesAllKnmKindsSelectOptions(state),
    responsibilityTypesSelectOptions: geCatalogsResponsibilityTypesSelectOptions(state),
    isUserTest: getUserProfileIsTest(state),
    isCoreLoading: getCoreIsLoading(state),
  };
};

export const KnmListFiltersModal = connect(mapStateToProps)(KnmListFiltersModalWithoutStore);
